import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { CreditCard, Loader2, CheckCircle2, AlertTriangle } from 'lucide-react';
import { log } from '../lib/logger';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Converge's demo Lightbox JS lives on apprule.net (per Elavon docs).
// When we move to production, flip to the prod script URL by checking
// an env var like REACT_APP_CONVERGE_ENV === 'prod'.
const CONVERGE_SCRIPT_DEMO = 'https://api.demo.convergepay.com/hosted-payments/PayWithConverge.js';
const CONVERGE_SCRIPT_PROD = 'https://api.convergepay.com/hosted-payments/PayWithConverge.js';

const loadConvergeScript = () => {
  if (window.PayWithConverge) return Promise.resolve(true);
  return new Promise((resolve, reject) => {
    const scriptUrl = process.env.REACT_APP_CONVERGE_ENV === 'prod'
      ? CONVERGE_SCRIPT_PROD
      : CONVERGE_SCRIPT_DEMO;
    const existing = document.querySelector(`script[src="${scriptUrl}"]`);
    if (existing) {
      existing.addEventListener('load', () => resolve(true));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.src = scriptUrl;
    s.async = true;
    s.onload = () => resolve(true);
    s.onerror = () => reject(new Error('Failed to load Converge Lightbox'));
    document.head.appendChild(s);
  });
};

/**
 * "Pay with Card" launcher — mints a Converge session token for the
 * given order, opens the Converge Lightbox modal, and on approval
 * reports the `ssl_txn_id` back to our `/payments/converge/approval`
 * endpoint so the order's `payment_status` flips to `authorized`.
 *
 * Usage:
 *   <ConvergePayButton
 *     orderId={orderId}
 *     amount={total}
 *     disabled={submitting}
 *     onAuthorized={() => toast.success('Card authorized')}
 *   />
 */
export const ConvergePayButton = ({
  orderId,
  amount,
  disabled = false,
  onAuthorized,
  onError,
  className = '',
  label = 'Pay with Card',
}) => {
  const [phase, setPhase] = useState('idle'); // idle | minting | open | authorized | error
  const [message, setMessage] = useState(null);

  useEffect(() => {
    // Pre-load the Converge script so the first click is instant.
    loadConvergeScript().catch((e) => log.warn('Converge script preload failed:', e));
  }, []);

  const handleAuthorized = useCallback(async (resp) => {
    // Forward the Lightbox payload to our backend, which persists the
    // ssl_txn_id + saved-card token (if present) against the order.
    try {
      await axios.post(`${API_URL}/api/payments/converge/approval`, resp);
      setPhase('authorized');
      setMessage('Card authorized — proceed to place your order.');
      onAuthorized?.(resp);
    } catch (e) {
      log.error('Approval forward to backend failed:', e);
      setPhase('error');
      setMessage(e?.response?.data?.detail || 'Backend could not record the authorization. Contact support.');
      onError?.(e);
    }
  }, [onAuthorized, onError]);

  const handleClick = async () => {
    if (disabled || !amount || !orderId) return;
    setPhase('minting');
    setMessage(null);
    try {
      await loadConvergeScript();
      const r = await axios.post(`${API_URL}/api/payments/converge/session`, {
        order_id: orderId,
        add_token: true,  // Save-card tokenization on by default
      });
      const token = r.data?.session_token;
      if (!token) throw new Error('Empty session token');

      setPhase('open');
      const paymentFields = { ssl_txn_auth_token: token };
      const callbacks = {
        onError: (err) => {
          log.error('Converge onError:', err);
          setPhase('error');
          setMessage(typeof err === 'string' ? err : (err?.errorMessage || 'Payment gateway error'));
          onError?.(err);
        },
        onCancelled: () => {
          setPhase('idle');
          setMessage('Payment cancelled.');
        },
        onDeclined: (resp) => {
          setPhase('error');
          setMessage(resp?.ssl_result_message || 'Card declined. Please try another card.');
          onError?.(resp);
        },
        onApproval: (resp) => handleAuthorized(resp),
      };
      // PayWithConverge.open is Converge's documented entry point for
      // the Lightbox. It opens an iframe modal over our page and
      // invokes the appropriate callback when the customer finishes.
      window.PayWithConverge.open(paymentFields, callbacks);
    } catch (e) {
      log.error('Converge session/open failed:', e);
      setPhase('error');
      setMessage(e?.response?.data?.detail || e?.message || 'Could not start payment.');
      onError?.(e);
    }
  };

  const busy = phase === 'minting' || phase === 'open';
  const success = phase === 'authorized';
  const errored = phase === 'error';

  return (
    <div className="flex flex-col gap-2">
      <button
        type="button"
        onClick={handleClick}
        disabled={disabled || busy || success}
        data-testid="converge-pay-btn"
        aria-busy={busy}
        className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-md font-semibold text-white transition-colors ${
          success
            ? 'bg-emerald-600 hover:bg-emerald-700'
            : 'bg-gradient-to-r from-[#4A7C59] to-[#2F5D3A] hover:from-[#3D6A4A] hover:to-[#1F4B2A]'
        } ${(disabled || busy || success) ? 'opacity-70 cursor-not-allowed' : ''} ${className}`}
      >
        {busy ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> {phase === 'minting' ? 'Preparing…' : 'Waiting for card…'}</>
        ) : success ? (
          <><CheckCircle2 className="w-4 h-4" /> Card authorized</>
        ) : (
          <><CreditCard className="w-4 h-4" /> {label}</>
        )}
      </button>
      {message && (
        <div
          role={errored ? 'alert' : 'status'}
          data-testid="converge-pay-message"
          className={`text-xs rounded px-3 py-2 flex items-start gap-2 ${
            errored
              ? 'text-red-700 bg-red-50 border border-red-200'
              : success
                ? 'text-emerald-800 bg-emerald-50 border border-emerald-200'
                : 'text-gray-700 bg-gray-50 border border-gray-200'
          }`}
        >
          {errored && <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />}
          <span>{message}</span>
        </div>
      )}
    </div>
  );
};

export default ConvergePayButton;
